import { useState, useCallback } from 'react'
import type { Quality } from '../types'

interface BulkDownloadFormProps {
  onSubmit: (urls: string[], quality: Quality) => void
  isLoading: boolean
}

const VALID_QUALITIES: Quality[] = ['highest', 'medium', 'low']

function isValidQuality(value: string): value is Quality {
  return VALID_QUALITIES.includes(value as Quality)
}

function isValidVideoUrl(url: string): boolean {
  try {
    const parsed = new URL(url.trim())
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

function parseUrls(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map(url => url.trim())
    .filter(url => url.length > 0)
}

export function BulkDownloadForm({ onSubmit, isLoading }: BulkDownloadFormProps) {
  const [urlsText, setUrlsText] = useState('')
  const [quality, setQuality] = useState<Quality>('highest')

  const urls = parseUrls(urlsText)
  const validUrls = urls.filter(isValidVideoUrl)
  const invalidCount = urls.length - validUrls.length

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (validUrls.length > 0) {
        onSubmit(validUrls, quality)
      }
    },
    [validUrls, quality, onSubmit]
  )

  const handleQualityChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value
      if (isValidQuality(value)) {
        setQuality(value)
      }
    },
    []
  )

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor="tweetUrls"
          className="block text-white/80 text-sm mb-2"
        >
          Video URLs (one per line, max 10)
        </label>
        <textarea
          id="tweetUrls"
          value={urlsText}
          onChange={(e) => setUrlsText(e.target.value)}
          placeholder={`https://twitter.com/user/status/123...\nhttps://youtube.com/watch?v=...\nhttps://instagram.com/reel/...`}
          disabled={isLoading}
          rows={6}
          className="w-full px-4 py-3 text-base bg-white/10 border-2 border-white/20 rounded-xl text-white placeholder-white/40 outline-none transition-colors focus:border-twitter-blue disabled:opacity-50 resize-none font-mono text-sm"
        />
        {urls.length > 0 && (
          <div className="mt-2 text-sm">
            <span className="text-success-green">{validUrls.length} valid</span>
            {invalidCount > 0 && (
              <span className="text-error-red ml-2">{invalidCount} invalid</span>
            )}
            {validUrls.length > 10 && (
              <span className="text-yellow-500 ml-2">(max 10, will use first 10)</span>
            )}
          </div>
        )}
      </div>

      <div>
        <label
          htmlFor="bulkQuality"
          className="block text-white/80 text-sm mb-2"
        >
          Video Quality
        </label>
        <select
          id="bulkQuality"
          value={quality}
          onChange={handleQualityChange}
          disabled={isLoading}
          className="w-full px-4 py-3.5 text-base bg-white/10 border-2 border-white/20 rounded-xl text-white outline-none appearance-none cursor-pointer disabled:opacity-50"
        >
          <option value="highest">Highest</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      <button
        type="submit"
        disabled={isLoading || validUrls.length === 0}
        className="w-full py-4 text-[17px] font-semibold bg-twitter-blue text-white rounded-xl transition-all hover:bg-twitter-blue-dark hover:-translate-y-0.5 disabled:bg-twitter-blue/50 disabled:cursor-not-allowed disabled:transform-none"
      >
        {isLoading
          ? 'Processing...'
          : `Download ${validUrls.length} Video${validUrls.length !== 1 ? 's' : ''} as ZIP`}
      </button>
    </form>
  )
}
